import { useCallback, useEffect, useMemo, useState } from 'react'
import { api, ApiError } from '../../api'
import { useI18n } from '../../i18n'
import type { Booking, BookingStatus } from '../../types'
import { addDays, formatHour, formatLong, parseYmd, todayYmd, ymd } from '../../lib/dates'
import { Avatar, Empty, RoleChip, Spinner, useConfirm, useToast } from '../../components/ui'
import { IconCheck, IconX } from '../../components/icons'

type Tab = 'pending' | 'approved'

export default function Approvals() {
  const toast = useToast()
  const confirm = useConfirm()
  const { t } = useI18n()
  const [bookings, setBookings] = useState<Booking[]>([])
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState<Tab>('pending')
  const [actingId, setActingId] = useState<string | null>(null)

  const load = useCallback(async () => {
    const today = parseYmd(todayYmd())
    try {
      const r = await api.bookings(ymd(addDays(today, -14)), ymd(addDays(today, 180)), 'all')
      setBookings(r.bookings)
    } catch {
      toast.push(t('ap.loadErr'), 'error')
    } finally {
      setLoading(false)
    }
  }, [toast])

  useEffect(() => {
    load()
  }, [load])

  async function decide(b: Booking, status: BookingStatus) {
    // Rejecting / removing permanently discards the booking — confirm first.
    if (status === 'rejected' && !(await confirm())) return
    setActingId(b.id)
    try {
      await api.updateBooking(b.id, status)
      setBookings((list) => list.map((x) => (x.id === b.id ? { ...x, status } : x)))
      toast.push(status === 'approved' ? t('ap.toast.approved') : status === 'rejected' ? t('ap.toast.rejected') : t('ot.toast.updated'), 'ok')
    } catch (err) {
      toast.push(err instanceof ApiError ? err.message : t('toast.updateErr'), 'error')
    } finally {
      setActingId(null)
    }
  }

  async function revert(b: Booking) {
    setActingId(b.id)
    try {
      await api.revertBooking(b.id)
      setBookings((list) => list.map((x) => (x.id === b.id ? { ...x, status: 'pending', offered_at: null } : x)))
      toast.push(t('ap.toast.reverted'), 'ok')
    } catch (err) {
      toast.push(err instanceof ApiError ? err.message : t('toast.updateErr'), 'error')
    } finally {
      setActingId(null)
    }
  }

  const pending = useMemo(() => bookings.filter((b) => b.status === 'pending'), [bookings])
  const approved = useMemo(() => bookings.filter((b) => b.status === 'approved'), [bookings])
  const list = tab === 'pending' ? pending : approved

  const grouped = useMemo(() => {
    const map: Record<string, Booking[]> = {}
    for (const b of [...list].sort((a, b) => (a.date + String(a.hour).padStart(2, '0')).localeCompare(b.date + String(b.hour).padStart(2, '0')))) {
      ;(map[b.date] ||= []).push(b)
    }
    return map
  }, [list])

  const dates = Object.keys(grouped).sort()

  if (loading) {
    return (
      <div className="full-loader" style={{ minHeight: 320 }}>
        <Spinner large />
      </div>
    )
  }

  return (
    <div>
      <div className="page-head between wrap">
        <div>
          <h1>{t('ap.title')}</h1>
          <p>{t('ap.subtitle')}</p>
        </div>
        <div className="segmented">
          <button className={tab === 'pending' ? 'active' : ''} onClick={() => setTab('pending')}>
            {t('ap.tab.pending')} {pending.length > 0 && `(${pending.length})`}
          </button>
          <button className={tab === 'approved' ? 'active' : ''} onClick={() => setTab('approved')}>
            {t('ap.tab.confirmed')} {approved.length > 0 && `(${approved.length})`}
          </button>
        </div>
      </div>

      {dates.length === 0 ? (
        <div className="card">
          <Empty title={tab === 'pending' ? t('ap.none.pending.title') : t('ap.none.confirmed.title')}>
            <p className="muted">
              {tab === 'pending' ? t('ap.none.pending.hint') : t('ap.none.confirmed.hint')}
            </p>
          </Empty>
        </div>
      ) : (
        <div className="col gap-16">
          {dates.map((date) => (
            <div className="card" key={date}>
              <div className="card-head">
                <h3>{formatLong(date)}</h3>
                <span className="tag">
                  {grouped[date].length === 1 ? t('ap.request1') : t('ap.requests', { n: grouped[date].length })}
                </span>
              </div>
              <div className="list">
                {grouped[date].map((b) => (
                  <div className="list-row" key={b.id}>
                    <Avatar name={b.user_name} email={b.user_email} green={tab === 'approved'} sm />
                    <div className="grow">
                      <div className="title">{b.user_name || b.user_email}</div>
                      <div className="sub">{formatHour(b.hour)}–{formatHour(b.hour + 1)}{b.note ? ` · ${b.note}` : ''}</div>
                    </div>
                    {b.role_name && <RoleChip name={b.role_name} color={b.role_color} />}
                    {tab === 'pending' ? (
                      <>
                        <button className="btn btn-primary btn-sm" onClick={() => decide(b, 'approved')} disabled={actingId === b.id}>
                          {actingId === b.id ? <Spinner /> : <><IconCheck width={16} height={16} /> {t('ap.approve')}</>}
                        </button>
                        <button className="btn btn-danger btn-sm" onClick={() => decide(b, 'rejected')} disabled={actingId === b.id}>
                          <IconX width={16} height={16} /> {t('ap.reject')}
                        </button>
                      </>
                    ) : (
                      <>
                        <span className="badge green">{t('status.approved')}</span>
                        <button className="btn btn-ghost btn-sm" onClick={() => revert(b)} disabled={actingId === b.id}>
                          {actingId === b.id ? <Spinner /> : t('ap.revert')}
                        </button>
                        <button className="btn btn-ghost btn-sm" onClick={() => decide(b, 'rejected')} disabled={actingId === b.id}>
                          {t('ap.remove')}
                        </button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
